import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';

// ─── Theme ────────────────────────────────────────────────────────────────────
const GOLD   = '#D4AF37';
const BG_CARD = '#0d1117';
const BORDER  = 'rgba(255,255,255,0.07)';
const TEXT_SEC = '#6b7280';
const GREEN   = '#10b981';
const BG      = '#050810';
const INPUT_BG = '#0a0f1e';

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const ICONS = {
  profile:   'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  booking:   'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  notif:     'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0',
  ai:        'M12 2a2 2 0 0 1 2 2v1h1a3 3 0 0 1 3 3v2a3 3 0 0 1-3 3h-1v1a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-1H9a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3h1V4a2 2 0 0 1 2-2zM9 10h6M9 13h4',
  appear:    'M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z',
  account:   'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const cardStyle = {
  background: BG_CARD,
  border: `1px solid ${BORDER}`,
  borderRadius: 16,
  padding: '24px',
  marginBottom: 16,
};

const inputStyle = {
  width: '100%',
  background: INPUT_BG,
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10,
  padding: '12px',
  color: '#fff',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block',
  color: TEXT_SEC,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  marginBottom: 8,
};

const rowStyle = {
  marginBottom: 20,
};

const SaveButton = ({ onClick, saving, label = 'Save Changes' }) => (
  <button
    onClick={onClick}
    disabled={saving}
    style={{
      background: saving ? '#333' : `linear-gradient(135deg, ${GOLD}, #b8941f)`,
      color: saving ? TEXT_SEC : '#000',
      fontWeight: 700,
      fontSize: 14,
      border: 'none',
      borderRadius: 10,
      padding: '13px 28px',
      cursor: saving ? 'not-allowed' : 'pointer',
      transition: 'opacity 0.2s',
      opacity: saving ? 0.7 : 1,
    }}
  >
    {saving ? 'Saving…' : label}
  </button>
);

const StatusMsg = ({ saved, error }) => (
  <>
    {saved && (
      <p style={{ color: GOLD, fontSize: 13, marginBottom: 12, fontWeight: 600 }}>
        Saved ✓
      </p>
    )}
    {error && (
      <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>
        {error}
      </p>
    )}
  </>
);

// iOS-style toggle
const Toggle = ({ checked, onChange }) => (
  <button
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    style={{
      width: 48,
      height: 28,
      borderRadius: 14,
      border: 'none',
      background: checked ? GOLD : 'rgba(255,255,255,0.1)',
      position: 'relative',
      cursor: 'pointer',
      transition: 'background 0.2s',
      flexShrink: 0,
      padding: 0,
    }}
  >
    <span style={{
      position: 'absolute',
      top: 3,
      left: checked ? 23 : 3,
      width: 22,
      height: 22,
      borderRadius: '50%',
      background: '#fff',
      transition: 'left 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
    }} />
  </button>
);

const ToggleRow = ({ label, desc, checked, onChange }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '14px 0',
    borderBottom: `1px solid ${BORDER}`,
  }}>
    <div>
      <div style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>{label}</div>
      {desc && <div style={{ color: TEXT_SEC, fontSize: 12, marginTop: 3 }}>{desc}</div>}
    </div>
    <Toggle checked={checked} onChange={onChange} />
  </div>
);

// ─── Tabs config ──────────────────────────────────────────────────────────────
const TABS = [
  { id: 'profile',  label: 'Profile',       icon: ICONS.profile  },
  { id: 'booking',  label: 'Booking Rules',  icon: ICONS.booking  },
  { id: 'notif',    label: 'Notifications',  icon: ICONS.notif    },
  { id: 'ai',       label: 'AI Assistant',   icon: ICONS.ai       },
  { id: 'appear',   label: 'Appearance',     icon: ICONS.appear   },
  { id: 'account',  label: 'Account',        icon: ICONS.account  },
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_HOURS = DAYS.reduce((acc, d) => ({
  ...acc,
  [d]: { open: d !== 'Sunday', start: '09:00', end: '18:00' },
}), {});

// ─── Profile Tab ──────────────────────────────────────────────────────────────
function ProfileTab({ logoSrc, setLogoSrc }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    shopName: '', barberName: '', phone: '', address: '', bio: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get('/settings').then(r => {
      const d = r.data;
      setForm({
        shopName:   d.shopName   || '',
        barberName: d.barberName || '',
        phone:      d.phone      || '',
        address:    d.address    || '',
        bio:        d.bio        || '',
      });
      if (d.logo) setLogoSrc(d.logo);
    }).catch(() => {});
  }, [setLogoSrc]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 512 * 1024) { setError('Image must be under 512KB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setLogoSrc(ev.target.result);
    reader.readAsDataURL(file);
    setError('');
  };

  const handleSave = async () => {
    setSaving(true); setSaved(false); setError('');
    try {
      await Promise.all([
        api.put('/settings', { key: 'shopName',   value: form.shopName }),
        api.put('/settings', { key: 'barberName', value: form.barberName }),
        api.put('/settings', { key: 'phone',      value: form.phone }),
        api.put('/settings', { key: 'address',    value: form.address }),
        api.put('/settings', { key: 'bio',        value: form.bio }),
        api.put('/settings/logo', { value: logoSrc }),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save. Check your connection.');
    } finally { setSaving(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText('https://joinmane.com/barber').catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {/* Shop info */}
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 20, marginTop: 0 }}>
          Shop Info
        </h2>
        {[
          { key: 'shopName',   label: 'Shop Name',    placeholder: 'Christian G Flair' },
          { key: 'barberName', label: 'Barber Name',   placeholder: 'Christian Gonzalez' },
          { key: 'phone',      label: 'Phone Number',  placeholder: '+1 (555) 000-0000' },
          { key: 'address',    label: 'Address',       placeholder: '123 Main St, Miami FL' },
        ].map(({ key, label, placeholder }) => (
          <div key={key} style={rowStyle}>
            <label style={labelStyle}>{label}</label>
            <input style={inputStyle} value={form[key]} onChange={set(key)} placeholder={placeholder} />
          </div>
        ))}
        <div style={rowStyle}>
          <label style={labelStyle}>
            Bio / Tagline
            <span style={{ float: 'right', fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: form.bio.length > 140 ? '#ef4444' : TEXT_SEC }}>
              {form.bio.length}/160
            </span>
          </label>
          <textarea
            style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }}
            value={form.bio}
            onChange={(e) => {
              if (e.target.value.length <= 160) set('bio')(e);
            }}
            placeholder="The best fade in Miami…"
            maxLength={160}
          />
        </div>
      </div>

      {/* Booking link */}
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 16, marginTop: 0 }}>
          Booking Link
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{
            background: INPUT_BG,
            border: `1px solid ${BORDER}`,
            borderRadius: 50,
            padding: '10px 18px',
            color: GOLD,
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: '0.01em',
          }}>
            joinmane.com/barber
          </div>
          <button
            onClick={copyLink}
            style={{
              background: copied ? GREEN : 'rgba(212,175,55,0.15)',
              border: `1px solid ${copied ? GREEN : GOLD}`,
              borderRadius: 50,
              padding: '10px 18px',
              color: copied ? '#fff' : GOLD,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      </div>

      {/* Logo upload */}
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 20, marginTop: 0 }}>
          Logo
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{
            width: 90, height: 90, borderRadius: 12,
            background: INPUT_BG, border: `1px solid ${BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0,
          }}>
            {logoSrc
              ? <img src={logoSrc} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} />
              : <span style={{ color: TEXT_SEC, fontSize: 11 }}>No logo</span>
            }
          </div>
          <div>
            <button
              onClick={() => fileRef.current?.click()}
              style={{
                background: 'rgba(212,175,55,0.12)',
                border: `1px solid ${GOLD}`,
                borderRadius: 8,
                color: GOLD,
                fontSize: 13,
                fontWeight: 600,
                padding: '9px 18px',
                cursor: 'pointer',
                marginBottom: 8,
                display: 'block',
              }}
            >
              {logoSrc ? 'Change Logo' : 'Upload Logo'}
            </button>
            {logoSrc && (
              <button
                onClick={() => setLogoSrc('')}
                style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 13, cursor: 'pointer', padding: 0 }}
              >
                Remove
              </button>
            )}
            <p style={{ color: TEXT_SEC, fontSize: 11, margin: '8px 0 0' }}>PNG or JPG · max 512KB</p>
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
        </div>
      </div>

      <StatusMsg saved={saved} error={error} />
      <SaveButton onClick={handleSave} saving={saving} />
    </>
  );
}

// ─── Booking Rules Tab ────────────────────────────────────────────────────────
function BookingTab() {
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [buffer, setBuffer] = useState('15');
  const [advance, setAdvance] = useState('30');
  const [requireDeposit, setRequireDeposit] = useState(false);
  const [depositAmount, setDepositAmount] = useState('20');
  const [autoApprove, setAutoApprove] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/settings').then(r => {
      const d = r.data;
      if (d.businessHours) setHours(d.businessHours);
      if (d.bufferTime)    setBuffer(String(d.bufferTime));
      if (d.advanceDays)   setAdvance(String(d.advanceDays));
      if (d.requireDeposit !== undefined) setRequireDeposit(!!d.requireDeposit);
      if (d.depositAmount)  setDepositAmount(String(d.depositAmount));
      if (d.autoApprove !== undefined) setAutoApprove(!!d.autoApprove);
    }).catch(() => {});
  }, []);

  const setDay = (day, field, val) =>
    setHours(h => ({ ...h, [day]: { ...h[day], [field]: val } }));

  const handleSave = async () => {
    setSaving(true); setSaved(false); setError('');
    try {
      await Promise.all([
        api.put('/settings', { key: 'businessHours', value: hours }),
        api.put('/settings', { key: 'bufferTime',    value: buffer }),
        api.put('/settings', { key: 'advanceDays',   value: advance }),
        api.put('/settings', { key: 'requireDeposit', value: requireDeposit }),
        api.put('/settings', { key: 'depositAmount',  value: depositAmount }),
        api.put('/settings', { key: 'autoApprove',    value: autoApprove }),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save. Check your connection.');
    } finally { setSaving(false); }
  };

  const selectStyle = {
    ...inputStyle,
    width: 'auto',
    minWidth: 120,
    cursor: 'pointer',
  };

  return (
    <>
      {/* Business Hours */}
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 20, marginTop: 0 }}>
          Business Hours
        </h2>
        {DAYS.map(day => (
          <div key={day} style={{
            display: 'grid',
            gridTemplateColumns: '110px 1fr',
            gap: 12,
            alignItems: 'center',
            marginBottom: 14,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Toggle
                checked={hours[day]?.open ?? true}
                onChange={(v) => setDay(day, 'open', v)}
              />
              <span style={{ color: hours[day]?.open ? '#fff' : TEXT_SEC, fontSize: 13, fontWeight: 500, transition: 'color 0.2s' }}>
                {day.slice(0, 3)}
              </span>
            </div>
            {hours[day]?.open ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="time"
                  value={hours[day]?.start || '09:00'}
                  onChange={e => setDay(day, 'start', e.target.value)}
                  style={{ ...inputStyle, width: 'auto', flex: 1, colorScheme: 'dark' }}
                />
                <span style={{ color: TEXT_SEC }}>–</span>
                <input
                  type="time"
                  value={hours[day]?.end || '18:00'}
                  onChange={e => setDay(day, 'end', e.target.value)}
                  style={{ ...inputStyle, width: 'auto', flex: 1, colorScheme: 'dark' }}
                />
              </div>
            ) : (
              <span style={{ color: TEXT_SEC, fontSize: 13 }}>Closed</span>
            )}
          </div>
        ))}
      </div>

      {/* Rules */}
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 20, marginTop: 0 }}>
          Booking Rules
        </h2>
        <div style={rowStyle}>
          <label style={labelStyle}>Buffer Between Appointments</label>
          <select style={selectStyle} value={buffer} onChange={e => setBuffer(e.target.value)}>
            <option value="0">No buffer</option>
            <option value="15">15 minutes</option>
            <option value="30">30 minutes</option>
            <option value="45">45 minutes</option>
            <option value="60">60 minutes</option>
          </select>
        </div>
        <div style={rowStyle}>
          <label style={labelStyle}>Max Advance Booking</label>
          <select style={selectStyle} value={advance} onChange={e => setAdvance(e.target.value)}>
            <option value="7">7 days</option>
            <option value="14">14 days</option>
            <option value="30">30 days</option>
            <option value="60">60 days</option>
            <option value="90">90 days</option>
          </select>
        </div>

        <ToggleRow
          label="Require Deposit"
          desc="Clients must pay a deposit to confirm booking"
          checked={requireDeposit}
          onChange={setRequireDeposit}
        />
        {requireDeposit && (
          <div style={{ ...rowStyle, marginTop: 16 }}>
            <label style={labelStyle}>Deposit Amount ($)</label>
            <input
              type="number"
              style={{ ...inputStyle, maxWidth: 140 }}
              value={depositAmount}
              onChange={e => setDepositAmount(e.target.value)}
              min="0"
              step="1"
            />
          </div>
        )}

        <ToggleRow
          label="Auto-Approve Appointments"
          desc="Skip pending state — bookings confirmed instantly"
          checked={autoApprove}
          onChange={setAutoApprove}
        />
      </div>

      <StatusMsg saved={saved} error={error} />
      <SaveButton onClick={handleSave} saving={saving} />
    </>
  );
}

// ─── Notifications Tab ────────────────────────────────────────────────────────
function NotificationsTab() {
  const [smsReminders, setSmsReminders]       = useState(true);
  const [reminderTiming, setReminderTiming]   = useState('24h');
  const [emailConfirm, setEmailConfirm]       = useState(true);
  const [followUp, setFollowUp]               = useState(false);
  const [noShowAlerts, setNoShowAlerts]       = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    api.get('/settings').then(r => {
      const d = r.data;
      if (d.smsReminders !== undefined)   setSmsReminders(!!d.smsReminders);
      if (d.reminderTiming)               setReminderTiming(d.reminderTiming);
      if (d.emailConfirm !== undefined)   setEmailConfirm(!!d.emailConfirm);
      if (d.followUp !== undefined)       setFollowUp(!!d.followUp);
      if (d.noShowAlerts !== undefined)   setNoShowAlerts(!!d.noShowAlerts);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true); setSaved(false); setError('');
    try {
      await Promise.all([
        api.put('/settings', { key: 'smsReminders',   value: smsReminders }),
        api.put('/settings', { key: 'reminderTiming',  value: reminderTiming }),
        api.put('/settings', { key: 'emailConfirm',    value: emailConfirm }),
        api.put('/settings', { key: 'followUp',        value: followUp }),
        api.put('/settings', { key: 'noShowAlerts',    value: noShowAlerts }),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save. Check your connection.');
    } finally { setSaving(false); }
  };

  return (
    <>
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 4, marginTop: 0 }}>
          Notifications
        </h2>
        <p style={{ color: TEXT_SEC, fontSize: 13, marginBottom: 20 }}>
          Control how you and your clients get notified.
        </p>

        <ToggleRow
          label="SMS Reminders"
          desc="Send clients a text reminder before their appointment"
          checked={smsReminders}
          onChange={setSmsReminders}
        />
        {smsReminders && (
          <div style={{ ...rowStyle, marginTop: 16, paddingLeft: 4 }}>
            <label style={labelStyle}>Remind clients how far in advance?</label>
            <select
              style={{ ...inputStyle, width: 'auto', minWidth: 180, cursor: 'pointer' }}
              value={reminderTiming}
              onChange={e => setReminderTiming(e.target.value)}
            >
              <option value="1h">1 hour before</option>
              <option value="2h">2 hours before</option>
              <option value="24h">24 hours before</option>
              <option value="48h">48 hours before</option>
            </select>
          </div>
        )}

        <ToggleRow
          label="Email Confirmations"
          desc="Email clients when a booking is confirmed or cancelled"
          checked={emailConfirm}
          onChange={setEmailConfirm}
        />
        <ToggleRow
          label="Follow-Up Messages"
          desc="Automatically follow up after appointments (CRM)"
          checked={followUp}
          onChange={setFollowUp}
        />
        <ToggleRow
          label="No-Show Alerts"
          desc="Notify you when a client is marked as a no-show"
          checked={noShowAlerts}
          onChange={setNoShowAlerts}
        />
      </div>

      <StatusMsg saved={saved} error={error} />
      <SaveButton onClick={handleSave} saving={saving} />
    </>
  );
}

// ─── AI Assistant Tab ─────────────────────────────────────────────────────────
function AITab() {
  const [phoneAgent, setPhoneAgent]         = useState(false);
  const [greeting, setGreeting]             = useState('');
  const [afterHours, setAfterHours]         = useState('voicemail');
  const [autoBook, setAutoBook]             = useState(false);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    api.get('/settings').then(r => {
      const d = r.data;
      if (d.phoneAgent !== undefined) setPhoneAgent(!!d.phoneAgent);
      if (d.aiGreeting)               setGreeting(d.aiGreeting);
      if (d.afterHours)               setAfterHours(d.afterHours);
      if (d.autoBook !== undefined)   setAutoBook(!!d.autoBook);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true); setSaved(false); setError('');
    try {
      await Promise.all([
        api.put('/settings', { key: 'phoneAgent', value: phoneAgent }),
        api.put('/settings', { key: 'aiGreeting', value: greeting }),
        api.put('/settings', { key: 'afterHours', value: afterHours }),
        api.put('/settings', { key: 'autoBook',   value: autoBook }),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save. Check your connection.');
    } finally { setSaving(false); }
  };

  return (
    <>
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 4, marginTop: 0 }}>
          AI Phone Agent
        </h2>
        <p style={{ color: TEXT_SEC, fontSize: 13, marginBottom: 20 }}>
          Let the AI handle incoming calls and book appointments automatically.
        </p>

        <ToggleRow
          label="Phone Agent"
          desc="Master switch — enable the AI to answer your phone"
          checked={phoneAgent}
          onChange={setPhoneAgent}
        />
      </div>

      {phoneAgent && (
        <>
          <div style={cardStyle}>
            <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 16, marginTop: 0 }}>
              Behavior
            </h2>
            <div style={rowStyle}>
              <label style={labelStyle}>AI Greeting Message</label>
              <textarea
                style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }}
                value={greeting}
                onChange={e => setGreeting(e.target.value)}
                placeholder="Hey, you've reached Christian G Flair! I'm your AI booking assistant. How can I help you today?"
              />
              <p style={{ color: TEXT_SEC, fontSize: 11, marginTop: 6 }}>
                This is what the AI says when it picks up.
              </p>
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>After-Hours Behavior</label>
              <select
                style={{ ...inputStyle, width: 'auto', minWidth: 200, cursor: 'pointer' }}
                value={afterHours}
                onChange={e => setAfterHours(e.target.value)}
              >
                <option value="voicemail">Take a voicemail</option>
                <option value="next_available">Offer next available slot</option>
                <option value="greet_only">Just greet and hang up</option>
              </select>
            </div>

            <ToggleRow
              label="Auto-Book from AI"
              desc="AI can confirm bookings directly — no manual approval needed"
              checked={autoBook}
              onChange={setAutoBook}
            />
          </div>
        </>
      )}

      <StatusMsg saved={saved} error={error} />
      <SaveButton onClick={handleSave} saving={saving} />
    </>
  );
}

// ─── Appearance Tab ───────────────────────────────────────────────────────────
function AppearanceTab({ logoSrc, setLogoSrc }) {
  const fileRef = useRef(null);
  const [accentColor, setAccentColor] = useState(GOLD);
  const [showReviews, setShowReviews] = useState(true);
  const [showPricing, setShowPricing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  const SWATCHES = [
    { color: '#D4AF37', label: 'Gold' },
    { color: '#3b82f6', label: 'Blue' },
    { color: '#8b5cf6', label: 'Purple' },
    { color: '#10b981', label: 'Green' },
  ];

  useEffect(() => {
    api.get('/settings').then(r => {
      const d = r.data;
      if (d.accentColor)               setAccentColor(d.accentColor);
      if (d.showReviews !== undefined)  setShowReviews(!!d.showReviews);
      if (d.showPricing !== undefined)  setShowPricing(!!d.showPricing);
    }).catch(() => {});
  }, []);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 512 * 1024) { setError('Image must be under 512KB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setLogoSrc(ev.target.result);
    reader.readAsDataURL(file);
    setError('');
  };

  const handleSave = async () => {
    setSaving(true); setSaved(false); setError('');
    try {
      await Promise.all([
        api.put('/settings/logo', { value: logoSrc }),
        api.put('/settings', { key: 'accentColor', value: accentColor }),
        api.put('/settings', { key: 'showReviews', value: showReviews }),
        api.put('/settings', { key: 'showPricing', value: showPricing }),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save. Check your connection.');
    } finally { setSaving(false); }
  };

  return (
    <>
      {/* Logo */}
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 16, marginTop: 0 }}>
          Logo
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div style={{
            width: 90, height: 90, borderRadius: 12,
            background: INPUT_BG, border: `1px solid ${BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0,
          }}>
            {logoSrc
              ? <img src={logoSrc} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} />
              : <span style={{ color: TEXT_SEC, fontSize: 11 }}>No logo</span>
            }
          </div>
          <div>
            <button
              onClick={() => fileRef.current?.click()}
              style={{
                background: 'rgba(212,175,55,0.12)',
                border: `1px solid ${GOLD}`,
                borderRadius: 8,
                color: GOLD,
                fontSize: 13,
                fontWeight: 600,
                padding: '9px 18px',
                cursor: 'pointer',
                display: 'block',
                marginBottom: 8,
              }}
            >
              {logoSrc ? 'Change Logo' : 'Upload Logo'}
            </button>
            {logoSrc && (
              <button
                onClick={() => setLogoSrc('')}
                style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 13, cursor: 'pointer', padding: 0 }}
              >
                Remove
              </button>
            )}
            <p style={{ color: TEXT_SEC, fontSize: 11, margin: '8px 0 0' }}>PNG or JPG · max 512KB</p>
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
        </div>
      </div>

      {/* Theme color */}
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 16, marginTop: 0 }}>
          Theme Accent Color
        </h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {SWATCHES.map(({ color, label }) => (
            <button
              key={color}
              onClick={() => setAccentColor(color)}
              title={label}
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: color,
                border: accentColor === color ? '3px solid #fff' : '3px solid transparent',
                cursor: 'pointer',
                boxShadow: accentColor === color ? `0 0 0 2px ${color}` : 'none',
                transition: 'border 0.15s, box-shadow 0.15s',
              }}
            />
          ))}
        </div>
        <p style={{ color: TEXT_SEC, fontSize: 12, marginTop: 12 }}>
          Selected: {SWATCHES.find(s => s.color === accentColor)?.label || 'Custom'}
        </p>
      </div>

      {/* Booking page */}
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 4, marginTop: 0 }}>
          Booking Page
        </h2>
        <p style={{ color: TEXT_SEC, fontSize: 13, marginBottom: 16 }}>
          What clients see on your public booking page.
        </p>
        <ToggleRow
          label="Show Reviews"
          desc="Display client reviews on your booking page"
          checked={showReviews}
          onChange={setShowReviews}
        />
        <ToggleRow
          label="Show Pricing"
          desc="Show service prices before clients book"
          checked={showPricing}
          onChange={setShowPricing}
        />
      </div>

      <StatusMsg saved={saved} error={error} />
      <SaveButton onClick={handleSave} saving={saving} />
    </>
  );
}

// ─── Account Tab ──────────────────────────────────────────────────────────────
function AccountTab() {
  const { username, logout } = useAuth();
  const [currentPw, setCurrentPw]   = useState('');
  const [newPw, setNewPw]           = useState('');
  const [confirmPw, setConfirmPw]   = useState('');
  const [pwSaving, setPwSaving]     = useState(false);
  const [pwSaved, setPwSaved]       = useState(false);
  const [pwError, setPwError]       = useState('');

  const handleChangePassword = async () => {
    if (!currentPw || !newPw || !confirmPw) {
      setPwError('Fill in all password fields.');
      return;
    }
    if (newPw !== confirmPw) {
      setPwError('New passwords do not match.');
      return;
    }
    if (newPw.length < 6) {
      setPwError('New password must be at least 6 characters.');
      return;
    }
    setPwSaving(true); setPwSaved(false); setPwError('');
    try {
      await api.put('/auth/change-password', { currentPassword: currentPw, newPassword: newPw });
      setPwSaved(true);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => setPwSaved(false), 3000);
    } catch (err) {
      setPwError(err?.response?.data?.error || 'Failed to change password.');
    } finally { setPwSaving(false); }
  };

  return (
    <>
      {/* User info */}
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 16, marginTop: 0 }}>
          Your Account
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            background: `linear-gradient(135deg, ${GOLD}, #b8941f)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#000', fontWeight: 800, fontSize: 18,
            flexShrink: 0,
          }}>
            {(username || 'U')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>{username || '—'}</div>
            <div style={{ color: TEXT_SEC, fontSize: 12 }}>Administrator</div>
          </div>
        </div>
      </div>

      {/* Change password */}
      <div style={cardStyle}>
        <h2 style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 20, marginTop: 0 }}>
          Change Password
        </h2>
        <div style={rowStyle}>
          <label style={labelStyle}>Current Password</label>
          <input
            type="password"
            style={inputStyle}
            value={currentPw}
            onChange={e => setCurrentPw(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
          />
        </div>
        <div style={rowStyle}>
          <label style={labelStyle}>New Password</label>
          <input
            type="password"
            style={inputStyle}
            value={newPw}
            onChange={e => setNewPw(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />
        </div>
        <div style={rowStyle}>
          <label style={labelStyle}>Confirm New Password</label>
          <input
            type="password"
            style={inputStyle}
            value={confirmPw}
            onChange={e => setConfirmPw(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />
        </div>

        {pwSaved && <p style={{ color: GOLD, fontSize: 13, marginBottom: 12, fontWeight: 600 }}>Password changed ✓</p>}
        {pwError && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{pwError}</p>}

        <SaveButton onClick={handleChangePassword} saving={pwSaving} label="Update Password" />
      </div>

      {/* Danger zone */}
      <div style={{
        ...cardStyle,
        border: '1px solid rgba(239,68,68,0.3)',
        background: 'rgba(239,68,68,0.05)',
      }}>
        <h2 style={{ color: '#ef4444', fontSize: 15, fontWeight: 700, marginBottom: 8, marginTop: 0 }}>
          Danger Zone
        </h2>
        <p style={{ color: TEXT_SEC, fontSize: 13, marginBottom: 20 }}>
          Sign out of all active sessions across all devices.
        </p>
        <button
          onClick={() => { if (typeof logout === 'function') logout(); }}
          style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.4)',
            borderRadius: 10,
            color: '#ef4444',
            fontWeight: 600,
            fontSize: 14,
            padding: '12px 24px',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
        >
          Sign Out Everywhere
        </button>
      </div>

      {/* Version */}
      <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
        <p style={{ color: TEXT_SEC, fontSize: 12 }}>
          Mane v1.0 · Built for Christian G Flair
        </p>
      </div>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [logoSrc, setLogoSrc]     = useState('');

  const renderTab = () => {
    switch (activeTab) {
      case 'profile':  return <ProfileTab logoSrc={logoSrc} setLogoSrc={setLogoSrc} />;
      case 'booking':  return <BookingTab />;
      case 'notif':    return <NotificationsTab />;
      case 'ai':       return <AITab />;
      case 'appear':   return <AppearanceTab logoSrc={logoSrc} setLogoSrc={setLogoSrc} />;
      case 'account':  return <AccountTab />;
      default:         return null;
    }
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <h1 style={{
          fontWeight: 900,
          fontSize: 'clamp(22px, 4vw, 30px)',
          letterSpacing: '-0.02em',
          marginBottom: 4,
          marginTop: 0,
          color: '#fff',
        }}>
          Settings
        </h1>
        <p style={{ color: TEXT_SEC, fontSize: 13, marginBottom: 28 }}>
          Manage your shop, bookings, and account.
        </p>

        {/* Tab bar */}
        <div style={{
          display: 'flex',
          gap: 0,
          overflowX: 'auto',
          borderBottom: `1px solid ${BORDER}`,
          marginBottom: 28,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}>
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
                  color: active ? GOLD : TEXT_SEC,
                  fontWeight: active ? 700 : 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.2s',
                  marginBottom: -1,
                  fontFamily: 'inherit',
                  flexShrink: 0,
                }}
              >
                <span style={{ opacity: active ? 1 : 0.6 }}>
                  <Icon d={tab.icon} size={15} />
                </span>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {renderTab()}
      </div>
    </AdminLayout>
  );
}
